import { ReactNode } from 'react';
import { Alert, Box, ContentLayout, Header, Spinner } from '@cloudscape-design/components';

import { usePermissions, RoleFlags } from '../hooks/use-permissions';

interface PermissionGuardProps {
  children: ReactNode;
  allow: (flags: RoleFlags) => boolean;
  message: string;
}

/**
 * Shared guard: a spinner while the session resolves, then either the page or an
 * access-denied message. Hiding a route is not authorisation on its own, the API
 * enforces the same rules for anything sensitive behind it.
 */
function PermissionGuard({ children, allow, message }: PermissionGuardProps) {
  const permissions = usePermissions();

  if (permissions.isLoading) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <Spinner size="large" />
      </Box>
    );
  }

  if (!allow(permissions)) {
    return (
      <ContentLayout header={<Header variant="h1">Access denied</Header>}>
        <Alert type="error" header="You don't have access to this page">
          {message}
        </Alert>
      </ContentLayout>
    );
  }

  return <>{children}</>;
}

/** Create and edit pages: not available to view-only users (campaign uploaders). */
export function WriteGuard({ children }: { children: ReactNode }) {
  return (
    <PermissionGuard
      allow={(p) => p.canWrite}
      message="Your role is view only for configurations. Contact your administrator if you believe you should have access."
    >
      {children}
    </PermissionGuard>
  );
}

/** Campaigns pages: administrators and campaign uploaders. */
export function CampaignGuard({ children }: { children: ReactNode }) {
  return (
    <PermissionGuard
      allow={(p) => p.canUploadCampaigns}
      message="This page is available to administrators and campaign uploaders only. Contact your administrator if you believe you should have access."
    >
      {children}
    </PermissionGuard>
  );
}
